import {
  IExperienceTemplateResource,
  IExperienceTemplateResourceGoogleDriveFolder,
  IExperienceTemplateResourceMicrosoftOneDriveFolder,
  IExperienceTemplateResourceMicrosoftSharepointFolder,
  ISubTaskSubmitTemplate,
  ITemplateResource,
  StorageContext,
  SubmitTargetType,
  SubTaskSubmitTemplateTarget,
  TemplateResourceType
} from '@dadohr/shared/domainTypes/experience';
import { Integration } from '@dadohr/shared/domainTypes/integrations';
import { CustomUserField } from '@dadohr/shared/domainTypes/org';
import {
  ListSubheader,
  MenuItem,
  TextField,
  Typography,
  useTheme
} from '@material-ui/core';
import { assertNever } from 'assert-never';
import { mapValues, sortBy } from 'lodash';
import React, { SetStateAction, useMemo, useState } from 'react';
import shortid from 'shortid';
import { AutocompleteSingle } from '../../../../../../../../components/Autocomplete';
import { DataFieldWithLabel } from '../../../../../../../../components/DataFieldWithLabel';
import {
  FormRow,
  FormRowGroup
} from '../../../../../../../../components/Form/FormRow';
import { InfoBox } from '../../../../../../../../components/InfoBox';
import { InfoCircle } from '../../../../../../../../components/InfoCircle';
import { InputFieldLoader } from '../../../../../../../../components/InputFieldLoader';
import { TokenDefinition } from '../../../../../../../../components/MarkdownText';
import { RadioPanel } from '../../../../../../../../components/RadioPanel';
import { SelectCustom } from '../../../../../../../../components/SelectCustom';
import { TextFieldWithReplacementTokens } from '../../../../../../../../components/TextFieldWithReplacementTokens';
import {
  useTranslation,
  useUnsafeTranslation
} from '../../../../../../../../i18next';
import { FlexContainer } from '../../../../../../../../layout/Flex';
import { useCurrentUser } from '../../../../../../../../services/currentUser';
import { LightMdToJsx } from '../../../../../../../../services/formatter';
import { useIntegrationsContext } from '../../../../../../../../services/integrations/context';
import {
  interpolate,
  userToInterpolation
} from '../../../../../../../../services/set/interpolation';
import { now } from '../../../../../../../../services/time';
import { EXAMPLE_USER_MAX } from '../../../../../../../../services/user';
import {
  useSharepointSites,
  useSiteDrives
} from '../../../../../../../Org/pages/Integrations/integrations/microsoft/sharepoint';
import { BlockSettingsSubPage, TaskBuilderContext } from '../../../types';
import {
  TaskBuilderBlockValidationState,
  TaskBuilderBlockValidationStateItem,
  useFieldValidator,
  VALIDATORS_SUBMIT
} from '../../../validation';
import { useEmailTokens } from '../common';
import { OnCompleteHooks, OnCompleteHooksReadOnly } from './onCompleteHooks';

export const SendToSelector = ({
  value,
  onChange,
  allowedTypes = [...Object.values(SubmitTargetType), 'EMAIL']
}: {
  value: SubTaskSubmitTemplateTarget['type'];
  onChange: (nextValue: SubTaskSubmitTemplateTarget['type']) => void;
  allowedTypes?: SubTaskSubmitTemplateTarget['type'][];
}) => {
  const { t } = useTranslation();
  const integrations = useIntegrationsContext();

  return (
    <SelectCustom
      label={t('Send to')}
      fullWidth
      value={value}
      onChange={(ev) => {
        onChange(ev.target.value as SubTaskSubmitTemplateTarget['type']);
      }}
    >
      {allowedTypes.includes('EMAIL') && (
        <MenuItem key="EMAIL" value="EMAIL">
          Email
        </MenuItem>
      )}
      {allowedTypes.includes(SubmitTargetType.GOOGLE_DRIVE) &&
        (value === SubmitTargetType.GOOGLE_DRIVE ||
          integrations.active.has(Integration.GOOGLE_SHEETS)) && (
          <MenuItem key="GDRIVE" value={SubmitTargetType.GOOGLE_DRIVE}>
            Google Drive
          </MenuItem>
        )}
      {allowedTypes.includes(SubmitTargetType.ONEDRIVE) &&
        (value === SubmitTargetType.ONEDRIVE ||
          integrations.active.has(Integration.MICROSOFT)) && (
          <MenuItem key="ONEDRIVE" value={SubmitTargetType.ONEDRIVE}>
            Microsoft OneDrive
          </MenuItem>
        )}

      {allowedTypes.includes(SubmitTargetType.SHAREPOINT) &&
        (value === SubmitTargetType.SHAREPOINT ||
          integrations.active.has(Integration.MICROSOFT)) && (
          <MenuItem key="SHAREPOINT" value={SubmitTargetType.SHAREPOINT}>
            Microsoft Sharepoint
          </MenuItem>
        )}
    </SelectCustom>
  );
};

type SharepointSiteDrive = {
  sharepointSiteId: string;
  sharepointSiteName: string;
  driveId?: string;
  driveName: string;
};

const SharepointDriveSelector = ({
  orgId,
  value,
  onChange
}: {
  value: SharepointSiteDrive;
  onChange: (nextValue: SharepointSiteDrive) => void;
  orgId: string;
}) => {
  const { t } = useUnsafeTranslation();
  const [drives, loadingDrives, errorDrives] = useSiteDrives(
    orgId,
    value.sharepointSiteId
  );

  return (
    <SelectCustom
      label={t('Site Drives')}
      value={value?.driveId || ''}
      loading={loadingDrives}
      onChange={(ev) => {
        const nextId = ev.target.value as string;
        const nextOption = drives?.find((s) => s.id === nextId);
        nextOption &&
          onChange({
            sharepointSiteId: value.sharepointSiteId,
            sharepointSiteName: value.sharepointSiteName,
            driveId: nextOption.id,
            driveName: nextOption.name
          });
      }}
      fullWidth
      renderInitialValueImmediately
    >
      <ListSubheader>Available Drives</ListSubheader>
      {drives &&
        drives.map((o) => (
          <MenuItem key={o.id} value={o.id}>
            {o.name}
          </MenuItem>
        ))}
    </SelectCustom>
  );
};

export const SharepointSiteAndDriveSelector = ({
  orgId,
  value,
  onChange
}: {
  value: Partial<SharepointSiteDrive> | null;
  onChange: (nextValue: SharepointSiteDrive) => void;
  orgId: string;
}) => {
  const [sites, loadingSites] = useSharepointSites(orgId);

  const { t } = useUnsafeTranslation();

  const sitesLabel = t('Sharepoint Sites');
  const options = useMemo(() => {
    if (!sites) {
      return null;
    }
    const list = [...sites];
    let selected = list.find((x) => x.id === value?.sharepointSiteId) || null;
    if (!selected && value?.sharepointSiteId) {
      const otherOption = {
        id: value.sharepointSiteId,
        displayName: value.sharepointSiteName || '',
        webUrl: ''
      };
      selected = otherOption;
      list.push(selected);
    }
    return {
      list: sortBy(list, (x) => x.displayName),
      selected
    };
  }, [sites, value]);

  return (
    <FlexContainer direction="column" fullWidth>
      {options ? (
        <AutocompleteSingle
          label={sitesLabel}
          value={options.selected}
          onChange={(nextOption) =>
            nextOption &&
            onChange({
              sharepointSiteId: nextOption.id,
              sharepointSiteName: nextOption.displayName,
              driveId: '',
              driveName: ''
            })
          }
          options={options.list}
          getOptionLabel={(o) => o.displayName}
          getOptionSelected={(x, o) => x.id === o.id}
          fullWidth
        />
      ) : (
        <InputFieldLoader label={sitesLabel} fullWidth />
      )}
      {value && value.sharepointSiteId && (
        <SharepointDriveSelector
          orgId={orgId}
          value={value as SharepointSiteDrive}
          onChange={onChange}
        />
      )}
    </FlexContainer>
  );
};

export const getGoogleDriveFolderResource = (
  res: IExperienceTemplateResource[]
): IExperienceTemplateResourceGoogleDriveFolder | null => {
  for (const r of res) {
    if (r.type === TemplateResourceType.GOOGLE_DRIVE_FOLDER) {
      return r;
    }
  }
  return null;
};

const getUniqueTemplateResource = <
  T extends ITemplateResource<TemplateResourceType>
>(
  res: IExperienceTemplateResource[],
  type: TemplateResourceType
) =>
  (res.find((r) => r.type === type) as T & IExperienceTemplateResource) || null;

const getMicrosoftOneDriveFolderResource = (
  res: IExperienceTemplateResource[]
) =>
  getUniqueTemplateResource<IExperienceTemplateResourceMicrosoftOneDriveFolder>(
    res,
    TemplateResourceType.MICROSOFT_ONEDRIVE_FOLDER
  );

const getMicrosoftSharepointFolderResource = (
  res: IExperienceTemplateResource[]
) =>
  getUniqueTemplateResource<IExperienceTemplateResourceMicrosoftSharepointFolder>(
    res,
    TemplateResourceType.MICROSOFT_SHAREPOINT_FOLDER
  );

export const BlockSettingsSubTaskSubmit = ({
  value,
  onChange,
  resources,
  onChangeResources,
  expTpl,
  validation,
  setValidation,
  customFields,
  goToSubPage,
  tokens,
  showOnCompleteHookSettings
}: {
  value: ISubTaskSubmitTemplate;
  onChange: (nextValue: ISubTaskSubmitTemplate) => void;
  resources: IExperienceTemplateResource[];
  onChangeResources: (nextResources: IExperienceTemplateResource[]) => void;
  expTpl: TaskBuilderContext;
  validation: TaskBuilderBlockValidationStateItem | null;
  setValidation: React.Dispatch<
    SetStateAction<TaskBuilderBlockValidationState>
  >;
  customFields: CustomUserField[];
  goToSubPage: (nextSubPage: BlockSettingsSubPage) => void;
  showOnCompleteHookSettings: boolean;
  tokens: TokenDefinition[];
}) => {
  const { t } = useUnsafeTranslation();
  const {
    org: { customUserFields, id: orgId }
  } = useCurrentUser();
  const interpolationArgs = useMemo(() => {
    return {
      experience: {
        title: expTpl.title,
        adminTitle: expTpl.adminTitle
      },
      label: value.data.label,
      participant: userToInterpolation(
        EXAMPLE_USER_MAX('', customUserFields),
        customUserFields,
        now() // TODO which date to use here?
      ),
      ...mapValues(Object.keys(expTpl.supportingActors), () =>
        EXAMPLE_USER_MAX('', customUserFields)
      )
    };
  }, [expTpl, customUserFields, value.data.label]);

  const emailTokens = useEmailTokens(tokens);
  const theme = useTheme();
  const initialRetentionRadioValue =
    value.data.to.type === 'EMAIL'
      ? 'CUSTOM'
      : (value.data.retentionTimeInDays || 0) === 0
      ? 'IMMEDIATE'
      : 'CUSTOM';

  const [retentionRadioValue, setRetentionRadioValue] = useState<
    'IMMEDIATE' | 'CUSTOM'
  >(initialRetentionRadioValue);

  const intitalRetentionInDaysTextFieldValue =
    value.data.to.type === 'EMAIL' ? 45 : value.data.retentionTimeInDays || 30;
  const [
    retentionTimeInDaysTextFieldValue,
    setRetentionTimeInDaysTextFieldValue
  ] = useState(intitalRetentionInDaysTextFieldValue);

  const validateEmail = useFieldValidator(
    setValidation,
    VALIDATORS_SUBMIT,
    'email'
  );
  const validateFileNameTemplate = useFieldValidator(
    setValidation,
    VALIDATORS_SUBMIT,
    'fileNameTemplate'
  );

  const validateHooks = useFieldValidator(
    setValidation,
    VALIDATORS_SUBMIT,
    'hooks'
  );

  const invalidEmailReason = validation?.fields.email || null;
  const invalidFileNameTemplateReason =
    validation?.fields.fileNameTemplate || null;

  const minRetention = value.data.to.type === 'EMAIL' ? 45 : 1;

  return (
    <>
      <FormRowGroup>
        <FormRow>
          <TextFieldWithReplacementTokens
            autoFocus
            label={t('Button Label')}
            value={value.data.label}
            fullWidth
            variant="outlined"
            onChange={(label) => {
              onChange({
                ...value,
                data: {
                  ...value.data,
                  label
                }
              });
            }}
            tokens={tokens}
          />
        </FormRow>
        <FormRow>
          <SendToSelector
            value={value.data.to.type}
            onChange={(nextType) => {
              if (nextType === 'EMAIL') {
                const nextRetentionTime = 45;
                setRetentionRadioValue('CUSTOM');
                setRetentionTimeInDaysTextFieldValue(nextRetentionTime);
                onChange({
                  ...value,
                  data: {
                    ...value.data,
                    retentionTimeInDays: nextRetentionTime,
                    to: {
                      type: nextType,
                      email: ''
                    }
                  }
                });
                return;
              }

              if (nextType === SubmitTargetType.GOOGLE_DRIVE) {
                let resource = getGoogleDriveFolderResource(resources);
                if (!resource) {
                  resource = {
                    type: TemplateResourceType.GOOGLE_DRIVE_FOLDER,
                    resourceId: shortid(),
                    name: '',
                    status: 'PENDING',
                    folderId: ''
                  };
                  onChangeResources([...resources, resource]);
                }

                const nextRetentionTime = 30;
                setRetentionRadioValue('IMMEDIATE');
                setRetentionTimeInDaysTextFieldValue(nextRetentionTime);
                const nextValue = {
                  ...value,
                  data: {
                    ...value.data,
                    retentionTimeInDays: nextRetentionTime,
                    to: {
                      type: nextType,
                      resourceId: resource.resourceId
                    }
                  }
                };
                validateEmail(nextValue, expTpl); // will reset the value
                onChange(nextValue);
                return;
              }

              if (nextType === SubmitTargetType.ONEDRIVE) {
                let resource = getMicrosoftOneDriveFolderResource(resources);
                if (!resource) {
                  resource = {
                    type: TemplateResourceType.MICROSOFT_ONEDRIVE_FOLDER,
                    resourceId: shortid(),
                    name: '',
                    publicUrl: '',
                    status: 'PENDING',
                    folderId: ''
                  };
                  onChangeResources([...resources, resource]);
                }
                const nextRetentionTime = 30;
                setRetentionRadioValue('IMMEDIATE');
                setRetentionTimeInDaysTextFieldValue(nextRetentionTime);

                const nextValue = {
                  ...value,
                  data: {
                    ...value.data,
                    retentionTimeInDays: nextRetentionTime,
                    to: {
                      type: nextType,
                      resourceId: resource.resourceId
                    }
                  }
                };
                validateEmail(nextValue, expTpl); // will reset the value
                onChange(nextValue);
                return;
              }

              if (nextType === SubmitTargetType.SHAREPOINT) {
                let resource = getMicrosoftSharepointFolderResource(resources);
                if (!resource) {
                  resource = {
                    type: TemplateResourceType.MICROSOFT_SHAREPOINT_FOLDER,
                    resourceId: shortid(),
                    name: '',
                    publicUrl: '',
                    status: 'PENDING',
                    folderId: '',
                    sharepointSiteId: '',
                    sharepointSiteName: '',
                    driveId: '',
                    driveName: ''
                  };
                  onChangeResources([...resources, resource]);
                }
                const nextValue = {
                  ...value,
                  data: {
                    ...value.data,
                    to: {
                      type: nextType,
                      resourceId: resource.resourceId
                    }
                  }
                };
                validateEmail(nextValue, expTpl); // will reset the value
                onChange(nextValue);
                return;
              }

              return assertNever(nextType);
            }}
          />
        </FormRow>

        {value.data.to.type === 'EMAIL' && (
          <FormRow>
            <TextFieldWithReplacementTokens
              label={t('Email address')}
              value={value.data.to.email}
              fullWidth
              variant="outlined"
              onBlur={() => validateEmail(value, expTpl)}
              error={!!invalidEmailReason}
              helperText={invalidEmailReason}
              onChange={(val) => {
                if (value.data.to.type !== 'EMAIL') {
                  // can never happen
                  return;
                }
                const nextValue = {
                  ...value,
                  data: {
                    ...value.data,
                    to: {
                      ...value.data.to,
                      email: val
                    }
                  }
                };
                invalidEmailReason && validateEmail(nextValue, expTpl);
                onChange(nextValue);
              }}
              tokens={emailTokens}
            />
          </FormRow>
        )}
        {value.data.to.type === SubmitTargetType.GOOGLE_DRIVE && (
          <FormRow>
            <TextField
              label={`${t('Folder name')}`}
              value={getGoogleDriveFolderResource(resources)?.name || ''}
              fullWidth
              helperText={`${t('Leave empty to use the Experience Title')}`}
              variant="outlined"
              onChange={(ev) => {
                const resource = getGoogleDriveFolderResource(resources);
                if (!resource) {
                  // can never happen
                  return;
                }
                const nextResources = resources.map((r) => {
                  if (r.resourceId !== resource.resourceId) {
                    return r;
                  }
                  return {
                    ...r,
                    name: ev.target.value
                  };
                });
                onChangeResources(nextResources);
              }}
            />
          </FormRow>
        )}
        {value.data.to.type === SubmitTargetType.ONEDRIVE && (
          <FormRow>
            <TextField
              label={`${t('Folder name')}`}
              value={getMicrosoftOneDriveFolderResource(resources)?.name || ''}
              fullWidth
              helperText={`${t(
                'You cannot use the name of an existing folder in this drive'
              )}`}
              variant="outlined"
              onChange={(ev) => {
                const resource = getMicrosoftOneDriveFolderResource(resources);
                if (!resource) {
                  // can never happen
                  return;
                }
                const nextResources = resources.map((r) => {
                  if (r.resourceId !== resource.resourceId) {
                    return r;
                  }
                  return {
                    ...r,
                    name: ev.target.value
                  };
                });
                onChangeResources(nextResources);
              }}
            />
          </FormRow>
        )}

        {value.data.to.type === SubmitTargetType.SHAREPOINT && (
          <FormRow>
            <SharepointSiteAndDriveSelector
              orgId={orgId}
              value={getMicrosoftSharepointFolderResource(resources)}
              onChange={(nextSharpointsite) => {
                const resource = getMicrosoftSharepointFolderResource(
                  resources
                );
                if (!resource) {
                  // can never happen
                  return;
                }

                const nextResources = resources.map((r) => {
                  if (
                    r.resourceId !== resource.resourceId ||
                    resource.type !==
                      TemplateResourceType.MICROSOFT_SHAREPOINT_FOLDER
                  ) {
                    return r;
                  }
                  const updatedResource = {
                    ...r,
                    sharepointSiteId: nextSharpointsite.sharepointSiteId,
                    sharepointSiteName: nextSharpointsite.sharepointSiteName,
                    driveName: nextSharpointsite.driveName,
                    driveId: nextSharpointsite.driveId
                  } as IExperienceTemplateResourceMicrosoftSharepointFolder;

                  return updatedResource;
                });
                onChangeResources(nextResources);
              }}
            />
          </FormRow>
        )}

        {value.data.to.type === SubmitTargetType.SHAREPOINT && (
          <FormRow>
            <TextField
              label={`${t('Folder name')}`}
              value={
                getMicrosoftSharepointFolderResource(resources)?.name || ''
              }
              fullWidth
              helperText={`${t(
                'You cannot use the name of an existing folder in this drive'
              )}`}
              variant="outlined"
              onChange={(ev) => {
                const resource = getMicrosoftSharepointFolderResource(
                  resources
                );
                if (!resource) {
                  // can never happen
                  return;
                }
                const nextResources = resources.map((r) => {
                  if (r.resourceId !== resource.resourceId) {
                    return r;
                  }
                  return {
                    ...r,
                    name: ev.target.value
                  };
                });
                onChangeResources(nextResources);
              }}
            />
          </FormRow>
        )}

        <FormRow>
          <TextFieldWithReplacementTokens
            label={t('Filename Template')}
            value={value.data.fileNameFormat}
            fullWidth
            variant="outlined"
            error={!!invalidFileNameTemplateReason}
            helperText={
              invalidFileNameTemplateReason ||
              `${t('Example')}: ${interpolate(
                value.data.fileNameFormat,
                interpolationArgs
              )}.pdf`
            }
            onBlur={() => validateFileNameTemplate(value, expTpl)}
            onChange={(fileNameFormat) => {
              const nextValue = {
                ...value,
                data: {
                  ...value.data,
                  fileNameFormat
                }
              };
              invalidFileNameTemplateReason &&
                validateFileNameTemplate(value, expTpl);
              onChange(nextValue);
            }}
            tokens={tokens}
          />
        </FormRow>

        <FormRow>
          <Typography variant="h6" gutterBottom>
            {t('Delete files from Dado’s temporary storage')}
          </Typography>
        </FormRow>
        <FormRow>
          <RadioPanel
            value={retentionRadioValue}
            orientation="vertical"
            variant="compact"
            onChange={(newRadioValue) => {
              if (newRadioValue === 'IMMEDIATE') {
                setRetentionRadioValue(newRadioValue);
                const nextValue: ISubTaskSubmitTemplate = {
                  ...value,
                  data: {
                    ...value.data,
                    retentionTimeInDays: 0
                  }
                };
                onChange(nextValue);
              } else if (newRadioValue === 'CUSTOM') {
                setRetentionRadioValue(newRadioValue);
                const nextValue: ISubTaskSubmitTemplate = {
                  ...value,
                  data: {
                    ...value.data,
                    retentionTimeInDays: retentionTimeInDaysTextFieldValue
                  }
                };
                onChange(nextValue);
              }
            }}
            options={[
              {
                label:
                  value.data.to.type === 'EMAIL'
                    ? t(`Immediately after successful delivery`)
                    : t(`Immediately after successful upload`),
                value: 'IMMEDIATE',
                disabled: value.data.to.type === 'EMAIL'
              },
              {
                value: 'CUSTOM',

                label: (
                  <FlexContainer direction="row">
                    {t('After')}{' '}
                    <TextField
                      type="number"
                      inputProps={{
                        min: minRetention
                      }}
                      style={{ width: theme.spacing(7) }}
                      variant="outlined"
                      disabled={retentionRadioValue !== 'CUSTOM'}
                      onBlur={(ev) => {
                        const nextRetentionTextValue = +ev.target.value;

                        if (nextRetentionTextValue < minRetention) {
                          console.log('moin');
                          setRetentionTimeInDaysTextFieldValue(minRetention);
                          if (retentionRadioValue === 'CUSTOM') {
                            console.log('moin2');
                            const nextValue: ISubTaskSubmitTemplate = {
                              ...value,

                              data: {
                                ...value.data,
                                retentionTimeInDays: minRetention
                              }
                            };
                            onChange(nextValue);
                          }
                        }
                      }}
                      onChange={(ev) => {
                        const nextRetentionTextValue = +ev.target.value;

                        setRetentionTimeInDaysTextFieldValue(
                          nextRetentionTextValue
                        );
                        if (retentionRadioValue === 'CUSTOM') {
                          const nextValue: ISubTaskSubmitTemplate = {
                            ...value,

                            data: {
                              ...value.data,
                              retentionTimeInDays: nextRetentionTextValue
                            }
                          };
                          onChange(nextValue);
                        }
                      }}
                      value={retentionTimeInDaysTextFieldValue}
                    />{' '}
                    {retentionTimeInDaysTextFieldValue === 1
                      ? t('day')
                      : t('days')}
                  </FlexContainer>
                )
              }
            ]}
          ></RadioPanel>
        </FormRow>
        {value.data.to.type === 'EMAIL' && (
          <FormRow>
            <InfoBox color="yellow">
              <FlexContainer alignItems="flex-start">
                <div style={{ marginTop: 2 }}>
                  <InfoCircle size={18} />
                </div>
                <div>
                  <LightMdToJsx component="div">
                    {t(
                      'Files are stored in Dado’s email provider for up to 45 days for traceability and to enable resending if initial delivery was unsuccessful.'
                    )}
                  </LightMdToJsx>
                </div>
              </FlexContainer>
            </InfoBox>
          </FormRow>
        )}
      </FormRowGroup>
      <OnCompleteHooks
        value={value.hooks.onComplete}
        tokens={tokens}
        onChange={(nextHooks) => {
          const nextValue = {
            ...value,
            hooks: {
              ...value.hooks,
              onComplete: nextHooks
            }
          };
          validateHooks(nextValue, expTpl);
          onChange(nextValue);
        }}
        resources={resources}
        onChangeResources={onChangeResources}
        expTpl={expTpl}
        customFields={customFields}
        goToSubPage={goToSubPage}
        validation={validation}
        showOnCompleteHookSettings={showOnCompleteHookSettings}
        storageContext={StorageContext.SUBTASK}
      />
    </>
  );
};

export const BlockSettingsSubTaskSubmitReadOnly = ({
  value,
  resources,
  expTpl,
  customFields,
  showOnCompleteHookSettings,
  tokens
}: {
  value: ISubTaskSubmitTemplate;
  resources: IExperienceTemplateResource[];
  expTpl: TaskBuilderContext;
  customFields: CustomUserField[];
  showOnCompleteHookSettings: boolean;
  tokens: TokenDefinition[];
}) => {
  const { t } = useUnsafeTranslation();
  return (
    <>
      <FormRowGroup>
        <FormRow>
          <DataFieldWithLabel label={t('Button Label')}>
            {value.data.label || '-'}
          </DataFieldWithLabel>
        </FormRow>
        <FormRow>
          <DataFieldWithLabel label={t('Send to')}>
            {value.data.to.type === 'EMAIL'
              ? 'Email'
              : value.data.to.type === SubmitTargetType.GOOGLE_DRIVE
              ? 'Google Drive'
              : value.data.to.type === SubmitTargetType.ONEDRIVE
              ? 'Microsoft OneDrive'
              : value.data.to.type === SubmitTargetType.SHAREPOINT
              ? 'Microsoft Sharepoint'
              : '-'}
          </DataFieldWithLabel>
        </FormRow>

        {value.data.to.type === 'EMAIL' && (
          <FormRow>
            <DataFieldWithLabel label={t('Email address')}>
              {value.data.to.email}
            </DataFieldWithLabel>
          </FormRow>
        )}

        {value.data.to.type === 'GDRIVE' && (
          <FormRow>
            <DataFieldWithLabel label={t('Folder name')}>
              {getGoogleDriveFolderResource(resources)?.name ||
                expTpl.adminTitle}
            </DataFieldWithLabel>
          </FormRow>
        )}

        {value.data.to.type === SubmitTargetType.ONEDRIVE && (
          <FormRow>
            <DataFieldWithLabel label={t('Folder name')}>
              {getMicrosoftOneDriveFolderResource(resources)?.name ||
                expTpl.adminTitle}
            </DataFieldWithLabel>
          </FormRow>
        )}

        {value.data.to.type === SubmitTargetType.SHAREPOINT && (
          <>
            <FormRow>
              <DataFieldWithLabel label={t('Sharepoint site')}>
                {getMicrosoftSharepointFolderResource(resources)
                  ?.sharepointSiteName || ''}
              </DataFieldWithLabel>
            </FormRow>
            <FormRow>
              <DataFieldWithLabel label={t('Drive name')}>
                {getMicrosoftSharepointFolderResource(resources)?.driveName ||
                  ''}
              </DataFieldWithLabel>
            </FormRow>
            <FormRow>
              <DataFieldWithLabel label={t('Folder name')}>
                {getMicrosoftSharepointFolderResource(resources)?.name ||
                  expTpl.adminTitle}
              </DataFieldWithLabel>
            </FormRow>
          </>
        )}

        <FormRow>
          <DataFieldWithLabel label={t('Filename Template')}>
            {value.data.fileNameFormat}
          </DataFieldWithLabel>
        </FormRow>
      </FormRowGroup>
      {showOnCompleteHookSettings && (
        <OnCompleteHooksReadOnly
          storageContext={StorageContext.SUBTASK}
          tokens={tokens}
          value={value.hooks.onComplete}
          resources={resources}
          expTpl={expTpl}
          customFields={customFields}
        />
      )}
    </>
  );
};
